import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError, getLocalUserFacingApiMessage, passThroughMessage } from "@/lib/api-client";
import { Sentry } from "@/lib/sentry";
import { createOrderErrorMessage } from "@/features/marketplace/lib/order-errors";
import i18n from "@/lib/i18n";
import {
  fetchVendors,
  fetchVendorDetails,
  fetchConnections,
  createConnection,
  updateAnswers,
  resignConnection,
  disconnectVendor,
  fetchOrders,
  fetchSentScripts,
  createOrder,
  retryOrder,
  updateOrderEta,
} from "@/features/marketplace/api/marketplace.api";
import type {
  CreateConnectionPayload,
  UpdateAnswersPayload,
  ResignConnectionPayload,
  CreateOrderPayload,
} from "@/features/marketplace/types/marketplace.types";
import {
  fetchAdminVendors,
  fetchAdminVendor,
  createAdminVendor,
  replaceAdminVendor,
  activateAdminVendor,
  approveVendorContactEmail,
  deactivateAdminVendor,
  revokeVendorContactEmail,
} from "@/features/admin/marketplace/api/admin-vendors.api";
import {
  fetchTeamRecipients,
  createTeamRecipient,
  updateTeamRecipient,
  deleteTeamRecipient,
} from "@/features/admin/marketplace/api/team-recipients.api";
import type {
  AdminVendorWritePayload,
  TeamRecipientCreatePayload,
  TeamRecipientUpdatePayload,
} from "@/features/admin/marketplace/types/admin-marketplace.types";

export const marketplaceKeys = {
  all: ["marketplace"] as const,
  vendors: (tenantId: string) => ["marketplace", "vendors", tenantId] as const,
  vendorDetails: (vendorId: string) =>
    ["marketplace", "vendor", vendorId, "details"] as const,
  connections: (tenantId: string) =>
    ["marketplace", "connections", tenantId] as const,
  orders: (tenantId: string) => ["marketplace", "orders", tenantId] as const,
  sentScripts: (tenantId: string) =>
    ["marketplace", "sent-scripts", tenantId] as const,
  adminVendors: ["marketplace", "admin", "vendors"] as const,
  adminVendor: (vendorId: string) =>
    ["marketplace", "admin", "vendor", vendorId] as const,
  adminTeamRecipients: ["marketplace", "admin", "team-recipients"] as const,
} as const;

const MILLISECONDS_PER_MINUTE = 60_000;
const MARKETPLACE_STALE_TIME_MINUTES = 5;
const MARKETPLACE_STALE_TIME_MS = MARKETPLACE_STALE_TIME_MINUTES * MILLISECONDS_PER_MINUTE;

export function vendorsQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.vendors(tid || "__none__"),
    queryFn: () => fetchVendors(tid),
    enabled: !!tid,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

/**
 * Vendor detail bundle (packages + questions) for the connect wizard.
 * Single round-trip covering step 2 (package picker) and step 3 (guidelines).
 */
export function vendorDetailsQueryOptions(vendorId: string | null | undefined) {
  const vid = vendorId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.vendorDetails(vid || "__none__"),
    queryFn: () => fetchVendorDetails(vid),
    enabled: !!vid,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function connectionsQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.connections(tid || "__none__"),
    queryFn: () => fetchConnections(tid),
    enabled: !!tid,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function ordersQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.orders(tid || "__none__"),
    queryFn: () => fetchOrders(tid),
    enabled: !!tid,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

/**
 * Map of scriptId → sent metadata for the active tenant. The Scripts tab
 * subscribes once on mount and renders badges synchronously from the cache.
 * Order-creation mutations invalidate this key so badges appear immediately.
 */
export function sentScriptsQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.sentScripts(tid || "__none__"),
    queryFn: () => fetchSentScripts(tid),
    enabled: !!tid,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function useConnectVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateConnectionPayload) => createConnection(payload),
    onSuccess: (_data, payload) => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.connections(payload.tenantId),
      });
      toast.success("Connected to vendor");
    },
  });
}

export function useUpdateAnswers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      connectionId: string;
      payload: UpdateAnswersPayload;
    }) => updateAnswers(args.connectionId, args.payload),
    onSuccess: (_data, args) => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.connections(args.payload.tenantId),
      });
      toast.success("Guidelines saved");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "TERMS_RESIGN_REQUIRED") {
        toast.error("Vendor terms have been updated — please re-sign first");
        return;
      }
      toast.error("Failed to save guidelines");
    },
    meta: { skipGlobalError: true },
  });
}

export function useResignConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      connectionId: string;
      payload: ResignConnectionPayload;
    }) => resignConnection(args.connectionId, args.payload),
    onSuccess: (_data, args) => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.connections(args.payload.tenantId),
      });
      toast.success("Terms signed");
    },
  });
}

export function useDisconnectVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { connectionId: string; tenantId: string }) =>
      disconnectVendor(args.connectionId, args.tenantId),
    onSuccess: (_data, args) => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.connections(args.tenantId),
      });
      toast.success("Disconnected from vendor");
    },
  });
}

function invalidateMarketplaceOrders(queryClient: ReturnType<typeof useQueryClient>, tenantId: string) {
  queryClient.invalidateQueries({ queryKey: marketplaceKeys.orders(tenantId) });
  queryClient.invalidateQueries({ queryKey: marketplaceKeys.sentScripts(tenantId) });
}

export function useCreateOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOrderPayload) => createOrder(payload),
    onSuccess: (_data, payload) => {
      invalidateMarketplaceOrders(queryClient, payload.tenantId);
    },
    onError: (err) => {
      toast.error(createOrderErrorMessage(err, i18n.t));
    },
    meta: { skipGlobalError: true },
  });
}

export function useRetryOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { orderId: string; tenantId: string }) =>
      retryOrder(args.orderId, args.tenantId),
    onSuccess: (_data, args) => {
      invalidateMarketplaceOrders(queryClient, args.tenantId);
      toast.success("Handoff retry queued");
    },
    onError: () => {
      toast.error("Couldn't retry this order");
    },
    meta: { skipGlobalError: true },
  });
}

function etaAttemptFingerprint(etaDate: string | null, etaNote: string | null): string {
  return JSON.stringify({ etaDate, etaNote });
}

export function useUpdateOrderEta() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const attemptKeys = useRef(new Map<string, { key: string; fingerprint: string }>());
  return useMutation({
    mutationFn: (args: {
      orderId: string;
      tenantId: string;
      etaDate: string | null;
      etaNote: string | null;
    }) => {
      const fingerprint = etaAttemptFingerprint(args.etaDate, args.etaNote);
      const existing = attemptKeys.current.get(args.orderId);
      const idempotencyKey =
        existing?.fingerprint === fingerprint ? existing.key : crypto.randomUUID();
      attemptKeys.current.set(args.orderId, { key: idempotencyKey, fingerprint });
      return updateOrderEta(args.orderId, {
        tenantId: args.tenantId,
        etaDate: args.etaDate,
        etaNote: args.etaNote,
        idempotencyKey,
      });
    },
    onSuccess: (result, args) => {
      attemptKeys.current.delete(args.orderId);
      queryClient.invalidateQueries({ queryKey: marketplaceKeys.orders(args.tenantId) });
      if (result.emailWarning) {
        toast.warning(
          t(
            "marketplace.orders.eta.ccEmptyWarning",
            "ETA saved. This order has no CC recipients, so no email was sent.",
          ),
        );
      }
    },
  });
}

export function adminVendorsQueryOptions() {
  return queryOptions({
    queryKey: marketplaceKeys.adminVendors,
    queryFn: fetchAdminVendors,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function adminVendorQueryOptions(vendorId: string | null | undefined) {
  const vid = vendorId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.adminVendor(vid || "__none__"),
    queryFn: () => fetchAdminVendor(vid),
    enabled: !!vid,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

function invalidateMarketplaceCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  vendorId?: string,
) {
  queryClient.invalidateQueries({ queryKey: marketplaceKeys.adminVendors });
  if (vendorId) {
    queryClient.invalidateQueries({
      queryKey: marketplaceKeys.adminVendor(vendorId),
    });
    queryClient.invalidateQueries({
      queryKey: marketplaceKeys.vendorDetails(vendorId),
    });
  }
  // Invalidate the whole marketplace tree since catalog list is keyed by tenantId.
  queryClient.invalidateQueries({ queryKey: marketplaceKeys.all });
}

function isVendorWriteTransportError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === "OFFLINE" ||
      error.code === "NETWORK_ERROR" ||
      error.code === "TIMEOUT")
  );
}

function shouldReportVendorWriteError(error: unknown): boolean {
  if (isVendorWriteTransportError(error)) return false;
  if (error instanceof ApiError) return error.status >= 500;
  return true;
}

function toastVendorWriteError(err: unknown) {
  if (isVendorWriteTransportError(err)) return;
  if (shouldReportVendorWriteError(err)) {
    Sentry.captureException(err);
  }
  const message = getLocalUserFacingApiMessage(err, passThroughMessage);
  if (message) toast.error(message);
}

export function useCreateAdminVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AdminVendorWritePayload) => createAdminVendor(payload),
    onSuccess: (data) => {
      invalidateMarketplaceCaches(queryClient, data.id);
      toast.success("Vendor created");
    },
    onError: toastVendorWriteError,
    meta: { skipGlobalError: true },
  });
}

export function useReplaceAdminVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { vendorId: string; payload: AdminVendorWritePayload }) =>
      replaceAdminVendor(args.vendorId, args.payload),
    onSuccess: (data) => {
      invalidateMarketplaceCaches(queryClient, data.id);
      toast.success("Vendor saved");
    },
    onError: toastVendorWriteError,
    meta: { skipGlobalError: true },
  });
}

export function useToggleAdminVendorActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { vendorId: string; activate: boolean }) =>
      args.activate
        ? activateAdminVendor(args.vendorId)
        : deactivateAdminVendor(args.vendorId),
    onSuccess: (data, args) => {
      invalidateMarketplaceCaches(queryClient, data.id);
      toast.success(args.activate ? "Vendor activated" : "Vendor deactivated");
    },
  });
}

export function useSetVendorContactEmailApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { vendorId: string; emailId: string; approve: boolean }) =>
      args.approve
        ? approveVendorContactEmail(args.vendorId, args.emailId)
        : revokeVendorContactEmail(args.vendorId, args.emailId),
    onSuccess: (_data, args) => {
      invalidateMarketplaceCaches(queryClient, args.vendorId);
      toast.success(args.approve ? "Address approved" : "Address revoked");
    },
    onError: (err) => {
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

// ─── Team notification recipients ───────────────────────────────────────────

export function adminTeamRecipientsQueryOptions() {
  return queryOptions({
    queryKey: marketplaceKeys.adminTeamRecipients,
    queryFn: fetchTeamRecipients,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

function invalidateTeamRecipients(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: marketplaceKeys.adminTeamRecipients });
}

export function useCreateTeamRecipient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TeamRecipientCreatePayload) => createTeamRecipient(payload),
    onSuccess: () => {
      invalidateTeamRecipients(queryClient);
      toast.success("Recipient added");
    },
    // Customer surface — duplicate 409 stays on the generic i18n message.
    onError: (err) => {
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

export function useUpdateTeamRecipient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { recipientId: string; payload: TeamRecipientUpdatePayload }) =>
      updateTeamRecipient(args.recipientId, args.payload),
    onSuccess: (data) => {
      invalidateTeamRecipients(queryClient);
      toast.success(data.isActive ? "Recipient resumed" : "Recipient paused");
    },
    onError: (err) => {
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

export function useDeleteTeamRecipient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (recipientId: string) => deleteTeamRecipient(recipientId),
    onSuccess: () => {
      invalidateTeamRecipients(queryClient);
      toast.success("Recipient removed");
    },
  });
}
